import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { Search, Trash2 } from 'lucide-react';
import localApiAdapter from '../../core/services/localApiAdapter';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { shouldShowCardsToolbarSearch } from '../../shared/utils/listPageSearchVisibility';
import {
  sliceQawaemStats,
  QAWAEM_KPI_GRID_CLASS,
} from '../../products/saudi/components/QawaemErpListPageShell';
import {
  HR_DEPARTMENTS_REFRESH_EVENT,
  HR_EMPLOYEES_REFRESH_EVENT,
  useHrApiRefreshListener,
} from './hrRefreshEvents';

export { sliceQawaemStats, QAWAEM_KPI_GRID_CLASS };

/** Standard 3-column form grid — HR create/edit dialogs. */
export const HR_FORM_GRID_CLASS = 'grid grid-cols-1 md:grid-cols-3 gap-4';

/** Lock body scroll while create/edit or view modals are open (ApprobationsCrudShell pattern). */
export function useHrModalBodyLock(showModal, showViewModal) {
  useEffect(() => {
    if (!showModal && !showViewModal) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showModal, showViewModal]);
}

/** Map HR stat config to FacturationListPageLayout stats (max 4). */
export function mapHrStatCards(entries = [], max = 4) {
  return sliceQawaemStats(entries, max).map((stat, i) => ({
    key: stat.key || stat.label || stat.title || i,
    label: stat.label || stat.title,
    value: stat.value,
    icon: stat.icon,
    variant: stat.variant || (i % 2 === 0 ? 'copper' : 'forest'),
    color: stat.color,
    className: stat.className,
  }));
}

/** Build column picker defs from { key, labelKey? } + label fn `p`. */
export function buildHrColumnDefs(columns, p) {
  return columns.map((col) => ({
    key: col.key,
    label: col.labelKey ? p(col.labelKey) : col.label,
    checked: col.checked !== false,
    required: col.required,
  }));
}

/** Initial visibility map for table columns (excludes actions). */
export function buildHrColumnVisibilityState(columns = []) {
  return Object.fromEntries(
    columns
      .filter((c) => c.key !== 'actions')
      .map((c) => [c.key, true]),
  );
}

/** Column picker defs bound to visibility state (for QawaemTableHeaderActionsCell). */
export function buildHrQawaemColumnDefs(columns, p, columnVisibility) {
  return buildHrColumnDefs(
    columns.map((col) => ({
      ...col,
      checked: col.key === 'actions' ? true : columnVisibility[col.key] !== false,
    })),
    p,
  );
}

export function isHrColumnVisible(columnVisibility, key) {
  return columnVisibility[key] !== false;
}

/** Alias for Products/facturation `showCol` pattern. */
export function showHrCol(columnVisibility, key) {
  return isHrColumnVisible(columnVisibility, key);
}

/** Guarded column toggle — matches useQawaemTableColumnVisibility. */
export function createHrHandleToggleColumn(setColumnVisibility, columns = []) {
  return (key) => {
    const col = columns.find((c) => c.key === key);
    if (col?.required || key === 'actions') return;
    setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
  };
}

/** Column visibility hook for HR list tables (QawaemTableHeaderActionsCell). */
export function useHrTableColumnVisibility(columns = [], p, t = null) {
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildHrColumnVisibilityState(columns),
  );

  const handleToggleColumn = useCallback(
    createHrHandleToggleColumn(setColumnVisibility, columns),
    [columns],
  );

  const isColVisible = useCallback(
    (key) => isHrColumnVisible(columnVisibility, key),
    [columnVisibility],
  );
  const showCol = isColVisible;

  const qawaemColumns = useMemo(() => {
    const defs = t
      ? columns.map((col) => (col.key === 'actions' ? { ...col, label: t('common.actions') } : col))
      : columns;
    return buildHrQawaemColumnDefs(defs, p, columnVisibility);
  }, [columns, p, t, columnVisibility]);

  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = hrTableColSpan(columnVisibility);

  return {
    columnVisibility,
    setColumnVisibility,
    handleToggleColumn,
    isColVisible,
    showCol,
    qawaemColumns,
    qawaemVisibleColumnCount,
    tableColSpan,
  };
}

/** Checkbox column + visible data columns + actions column. */
export function hrTableColSpan(columnVisibility) {
  const visibleData = Object.values(columnVisibility).filter((v) => v !== false).length;
  return 1 + visibleData + 1;
}

/** Row selection helpers for list tables (Products.js / EmployeePortalCrudShell pattern). */
export function useHrTableRowSelection(filteredRecords, selectedRecords, setSelectedRecords) {
  const filteredIds = useMemo(() => filteredRecords.map((r) => r.id), [filteredRecords]);
  const allRowsSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = useCallback(() => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredIds);
    }
  }, [allRowsSelected, someRowsSelected, filteredIds, setSelectedRecords]);

  const toggleRecord = useCallback(
    (id) => setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])),
    [setSelectedRecords],
  );

  return { filteredIds, allRowsSelected, someRowsSelected, handleSelectAll, toggleRecord };
}

/** Bulk delete button for FacturationListPageLayout filterActions slot. */
export function HrBulkDeleteFilterAction({ count, onDelete, label }) {
  if (!count) return null;
  return (
    <button
      type="button"
      onClick={onDelete}
      className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
    >
      <Trash2 size={16} />
      <span>{label}</span>
    </button>
  );
}

/** Legacy controls strip — search visible in card view only (FacturationListPageLayout pattern). */
export function buildHrControlsStrip({
  qawaem,
  viewMode,
  onViewModeChange,
  isDashboardLightMode,
  searchTerm,
  onSearchChange,
  searchPlaceholder,
  cardsTitle,
  tableTitle,
}) {
  if (qawaem) return null;

  return (
    <div className="facturation-list-controls-strip p-4 border-b border-white/10">
      <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
        {shouldShowCardsToolbarSearch(viewMode) ? (
          <div className="relative min-w-0 flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        ) : null}
        <FacturationViewModeToggle
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          isLight={isDashboardLightMode}
          cardsTitle={cardsTitle}
          tableTitle={tableTitle}
        />
      </div>
    </div>
  );
}

const hrImportExportNoop = () => {};

/** Import/export toolbar props — pass `handlers` when the page supports I/O. */
/**
 * Expected create/edit modal tab count from field count.
 * ≤8 fields → 0 (no tabs), 9–17 → 2 tabs, ≥18 → 2–3 tabs.
 */
export function getHrFormModalTabCount(fieldCount) {
  if (fieldCount <= 8) return 0;
  if (fieldCount <= 17) return 2;
  return 3;
}

/** Tab bar for HR create/edit modals (Products.js pattern — tabs, not accordion). */
export function HrFormModalTabBar({ tabs, activeTab, onTabChange, isLight = false, ariaLabel }) {
  return (
    <div className={`shrink-0 px-6 pt-3 border-b ${isLight ? 'border-gray-200' : 'border-white/10'}`}>
      <div className="flex flex-wrap gap-1" role="tablist" aria-label={ariaLabel}>
        {tabs.map((tab) => {
          const TabIcon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onTabChange(tab.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${
                active
                  ? (isLight
                    ? 'bg-[#6e9b6e]/15 text-forest-900 border-b-2 border-[#6e9b6e] -mb-px'
                    : 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px')
                  : (isLight
                    ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80'
                    : 'text-white/55 hover:text-white hover:bg-white/5')
              }`}
            >
              {TabIcon ? (
                <TabIcon
                  size={16}
                  className={active ? (tab.iconClassName || 'text-blue-400') : ''}
                />
              ) : null}
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Display name from HR employee record (API `prenom`/`nom` or normalized `firstName`/`lastName`). */
export function formatHrEmployeeLabel(emp) {
  if (!emp) return '';
  const first = emp.firstName || emp.first_name || emp.prenom || '';
  const last = emp.lastName || emp.last_name || emp.nom || '';
  const fromParts = `${first} ${last}`.trim();
  return fromParts || emp.name || emp.fullName || '';
}

/** Find employee by display label. */
export function resolveHrEmployeeFromLabel(employees = [], label) {
  if (!label) return null;
  return employees.find((emp) => formatHrEmployeeLabel(emp) === label) || null;
}

/** Normalize GET /hr/employees response to an array. */
export function normalizeHrEmployeesResponse(response) {
  const raw = response?.data ?? response ?? [];
  return Array.isArray(raw) ? raw : [];
}

/** Fetch employees from the shared HR API. */
export async function fetchHrEmployees() {
  const response = await localApiAdapter.get('/hr/employees');
  return normalizeHrEmployeesResponse(response);
}

/** Hook — loads /hr/employees on mount; refreshes on HR_EMPLOYEES_REFRESH_EVENT. */
export function useHrEmployees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setEmployees(await fetchHrEmployees());
    } catch (error) {
      console.error('Error loading HR employees:', error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useHrApiRefreshListener(HR_EMPLOYEES_REFRESH_EVENT, refresh);

  return { employees, loading, refresh };
}

/** Preview next matricule — matches backend nextHrEmployeeMatricule (PREFIX01, PREFIX02, …). */
export function previewHrEmployeeMatricule(prefix = 'EMP', employees = []) {
  const safePrefix = String(prefix || 'EMP').trim() || 'EMP';
  const escaped = safePrefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  let maxNumber = 0;
  for (const emp of Array.isArray(employees) ? employees : []) {
    const raw = String(emp?.mat ?? '').trim();
    if (!raw) continue;

    const noSeparator = raw.match(new RegExp(`^${escaped}(\\d+)$`, 'i'));
    if (noSeparator) {
      maxNumber = Math.max(maxNumber, parseInt(noSeparator[1], 10) || 0);
      continue;
    }

    const dashed = raw.match(new RegExp(`^${escaped}-(\\d+)$`, 'i'));
    if (dashed) {
      maxNumber = Math.max(maxNumber, parseInt(dashed[1], 10) || 0);
      continue;
    }

    const trailing = raw.match(/(\d+)$/);
    if (trailing) {
      maxNumber = Math.max(maxNumber, parseInt(trailing[1], 10) || 0);
    }
  }

  return `${safePrefix}${String(maxNumber + 1).padStart(2, '0')}`;
}

/** Normalize GET /management/departments response to an array. */
export function normalizeHrDepartmentsResponse(response, { includeInactive = false } = {}) {
  const raw = response?.data ?? response ?? [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((d) => ({
      ...d,
      id: d.id || d.name,
      name: d.name || '',
    }))
    .filter((d) => includeInactive || d.status === 'active' || !d.status);
}

/** Fetch departments from the shared management API. */
export async function fetchHrDepartments(options = {}) {
  const response = await localApiAdapter.get('/management/departments');
  return normalizeHrDepartmentsResponse(response, options);
}

/** Hook — loads /management/departments on mount; refreshes on HR_DEPARTMENTS_REFRESH_EVENT. */
export function useHrDepartments(options = {}) {
  const { includeInactive = false } = options;
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setDepartments(await fetchHrDepartments({ includeInactive }));
    } catch (error) {
      console.error('Error loading HR departments:', error);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useHrApiRefreshListener(HR_DEPARTMENTS_REFRESH_EVENT, refresh);

  return { departments, loading, refresh };
}

/** Resolve employee by id, mat, or display name. */
export function resolveHrEmployeeRecord(employees = [], lookup = {}) {
  const list = Array.isArray(employees) ? employees : [];
  const { id, mat, name, employee_id: employeeIdSnake, employeeId } = lookup || {};
  const idVal = id ?? employeeId ?? employeeIdSnake;
  if (idVal != null && String(idVal).trim()) {
    const byId = list.find((e) => String(e.id) === String(idVal));
    if (byId) return byId;
    const byMat = list.find((e) => String(e.mat) === String(idVal));
    if (byMat) return byMat;
  }
  if (mat != null && String(mat).trim()) {
    const byMatOnly = list.find((e) => String(e.mat) === String(mat));
    if (byMatOnly) return byMatOnly;
  }
  if (name) {
    return resolveHrEmployeeFromLabel(list, name);
  }
  return null;
}

/** Matricule (رمز) for tables — prefers linked employee, falls back to stored id. */
export function resolveHrEmployeeMatricule(employees = [], record = {}) {
  const emp = resolveHrEmployeeRecord(employees, {
    id: record.employee_id ?? record.employeeId,
    mat: record.employee_mat ?? record.employeeMat,
    name: record.employee_name ?? record.employeeName,
  });
  return emp?.mat || record.employee_mat || record.employeeMat || '';
}

/** Standard picker onChange payload — id FK + display name + matricule. */
export function buildHrEmployeePickerPatch(selectedEmployee, nameKey = 'employee_name', idKey = 'employee_id') {
  if (!selectedEmployee) {
    return { [nameKey]: '', [idKey]: '', employeeId: '', employeeName: '', employeeMat: '' };
  }
  const label = formatHrEmployeeLabel(selectedEmployee);
  const id = selectedEmployee.id != null ? String(selectedEmployee.id) : '';
  const mat = selectedEmployee.mat || '';
  return {
    [nameKey]: label,
    [idKey]: id,
    employeeId: id,
    employeeName: label,
    employeeMat: mat,
  };
}

/** CustomSelect options for HR employee pickers. */
export function buildHrEmployeeSelectOptions(employees = [], emptyOption = null) {
  const opts = employees
    .map((emp) => {
      const label = formatHrEmployeeLabel(emp);
      if (!label) return null;
      const matSuffix = emp.mat ? ` (${emp.mat})` : '';
      return { value: label, label: `${label}${matSuffix}` };
    })
    .filter(Boolean);
  return emptyOption ? [emptyOption, ...opts] : opts;
}

/** Resolve department id/name to Arabic label for lists and charts. */
export function resolveHrDepartmentLabel(departments = [], affectationIdOrName) {
  if (!affectationIdOrName) return '';
  const dept = departments.find(
    (d) => d.id === affectationIdOrName || d.name === affectationIdOrName,
  );
  return dept?.name || affectationIdOrName;
}

/** Cap list rows to configured itemsPerPage. */
export function sliceHrListItems(items = [], itemsPerPage = 50) {
  const limit = Math.max(1, parseInt(itemsPerPage, 10) || 50);
  return items.slice(0, limit);
}

export function buildHrImportExportProps({
  showImportDropdown,
  setShowImportDropdown,
  showExportDropdown,
  setShowExportDropdown,
  importDropdownRef,
  exportDropdownRef,
  p,
  t,
  importLabel,
  exportLabel,
  generatingLabel,
  isGeneratingPDF = false,
  handlers = {},
}) {
  return {
    importOpen: showImportDropdown,
    exportOpen: showExportDropdown,
    onToggleImport: () => {
      setShowImportDropdown(!showImportDropdown);
      setShowExportDropdown(false);
    },
    onToggleExport: () => {
      setShowExportDropdown(!showExportDropdown);
      setShowImportDropdown(false);
    },
    importRef: importDropdownRef,
    exportRef: exportDropdownRef,
    importLabel: importLabel || p?.('import', { defaultValue: t?.('common.import', { defaultValue: 'استيراد' }) }),
    exportLabel: exportLabel || t?.('common.export', { defaultValue: 'تصدير' }),
    generatingLabel: generatingLabel || p?.('generating', { defaultValue: t?.('common.loading', { defaultValue: 'جاري الإنشاء...' }) }),
    isGeneratingPDF,
    onImportCSV: handlers.onImportCSV || handlers.onImport || hrImportExportNoop,
    onImportExcel: handlers.onImportExcel || handlers.onImport || hrImportExportNoop,
    onImportJSON: handlers.onImportJSON || hrImportExportNoop,
    onExportPDF: handlers.onExportPDF || hrImportExportNoop,
    onExportExcel: handlers.onExportExcel || hrImportExportNoop,
    onExportCSV: handlers.onExportCSV || handlers.onExport || hrImportExportNoop,
    onExportJSON: handlers.onExportJSON || hrImportExportNoop,
    showJsonImportExport: handlers.showJsonImportExport ?? false,
  };
}
